import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Search, BarChart3 } from 'lucide-react';
import TimeFilterControls, { TimeFilter } from './TimeFilterControls';

interface SearchTimelineData {
  date: string;
  searches: number;
  completed: number;
  failed: number;
  processing: number;
}

interface SearchStatistics {
  total_all_time: number;
  period_total: number;
  average_per_day: number;
  completion_rate: number;
}

interface SearchAnalyticsData {
  timeline: SearchTimelineData[];
  total_searches: number;
  statistics: SearchStatistics;
  period_start: string | null;
  period_end: string | null;
}

const SearchAnalytics: React.FC = () => {
  const [data, setData] = useState<SearchAnalyticsData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [timeFilter, setTimeFilter] = useState<TimeFilter>({});
  const previousFilterRef = useRef<string>('');

  const fetchSearchAnalytics = useCallback(async (filter: TimeFilter) => {
    try {
      setLoading(true);
      setError(null);

      const apiUrl = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('auth_token');
      const headers: HeadersInit = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const params = new URLSearchParams();
      if (filter.limitDays) {
        params.append('limit_days', filter.limitDays.toString());
      }
      if (filter.startDate) {
        params.append('start_date', filter.startDate);
      }
      if (filter.endDate) {
        params.append('end_date', filter.endDate);
      }

      const url = `${apiUrl}/api/admin/analytics/searches/timeline?${params.toString()}`;
      const response = await fetch(url, { headers });

      if (!response.ok) {
        throw new Error(`Failed to fetch search analytics: ${response.status}`);
      }

      const analyticsData = await response.json();
      setData(analyticsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch search analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const filterKey = JSON.stringify(timeFilter);
    if (filterKey !== previousFilterRef.current) {
      previousFilterRef.current = filterKey;
      fetchSearchAnalytics(timeFilter);
    }
  }, [timeFilter, fetchSearchAnalytics]);

  const handleFilterChange = useCallback((filter: TimeFilter) => {
    setTimeFilter(filter);
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <span className="ml-3 text-gray-600">Loading search analytics...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-red-200 p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-600">Error: {error}</p>
        </div>
      </div>
    );
  }

  if (!data || !data.timeline || data.timeline.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <TimeFilterControls onFilterChange={handleFilterChange} defaultFilter={timeFilter} />
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <BarChart3 className="h-12 w-12 mb-4" />
          <p className="text-lg font-medium">No search data available</p>
          <p className="text-sm">Check back once searches are performed</p>
        </div>
      </div>
    );
  }

  const processedData = data.timeline.map((item) => ({
    ...item,
    formattedDate: new Date(item.date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    }),
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900">{new Date(data.date).toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}</p>
          <p className="text-indigo-600">
            <span className="font-medium">{data.searches}</span> total searches
          </p>
          <p className="text-green-600">
            <span className="font-medium">{data.completed}</span> completed
          </p>
          <p className="text-yellow-600">
            <span className="font-medium">{data.processing}</span> processing
          </p>
          <p className="text-red-600">
            <span className="font-medium">{data.failed}</span> failed
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <TimeFilterControls onFilterChange={handleFilterChange} defaultFilter={timeFilter} />

      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Search className="h-6 w-6 text-indigo-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Search Analytics</h3>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-indigo-50 rounded-lg p-4">
            <div className="text-sm font-medium text-indigo-600">Total Searches (All Time)</div>
            <div className="text-2xl font-bold text-indigo-900">{data.total_searches}</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-sm font-medium text-blue-600">Period Total</div>
            <div className="text-2xl font-bold text-blue-900">{data.statistics.period_total}</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="text-sm font-medium text-green-600">Avg Per Day</div>
            <div className="text-2xl font-bold text-green-900">
              {data.statistics.average_per_day.toFixed(1)}
            </div>
          </div>
          <div className="bg-amber-50 rounded-lg p-4">
            <div className="text-sm font-medium text-amber-600">Completion Rate</div>
            <div className="text-2xl font-bold text-amber-900">
              {(data.statistics.completion_rate * 100).toFixed(1)}%
            </div>
          </div>
        </div>

        <div className="mb-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">Search Activity Timeline</h4>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={processedData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="formattedDate"
                  stroke="#6b7280"
                  fontSize={12}
                  tick={{ fill: '#6b7280' }}
                />
                <YAxis
                  stroke="#6b7280"
                  fontSize={12}
                  tick={{ fill: '#6b7280' }}
                  allowDecimals={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="searches"
                  stroke="#4f46e5"
                  strokeWidth={2}
                  name="Total Searches"
                  dot={{ fill: '#4f46e5', strokeWidth: 2, r: 3 }}
                />
                <Line
                  type="monotone"
                  dataKey="completed"
                  stroke="#10b981"
                  strokeWidth={2}
                  name="Completed"
                  dot={{ fill: '#10b981', strokeWidth: 2, r: 3 }}
                />
                <Line
                  type="monotone"
                  dataKey="processing"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  name="Processing"
                  dot={{ fill: '#f59e0b', strokeWidth: 2, r: 3 }}
                />
                <Line
                  type="monotone"
                  dataKey="failed"
                  stroke="#ef4444"
                  strokeWidth={2}
                  name="Failed"
                  dot={{ fill: '#ef4444', strokeWidth: 2, r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div>
          <h4 className="text-md font-medium text-gray-900 mb-4">Search Activity (Bar Chart)</h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={processedData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="formattedDate"
                  stroke="#6b7280"
                  fontSize={12}
                  tick={{ fill: '#6b7280' }}
                />
                <YAxis
                  stroke="#6b7280"
                  fontSize={12}
                  tick={{ fill: '#6b7280' }}
                  allowDecimals={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="searches" fill="#4f46e5" name="Total" radius={[4, 4, 0, 0]} />
                <Bar dataKey="completed" fill="#10b981" name="Completed" radius={[4, 4, 0, 0]} />
                <Bar dataKey="processing" fill="#f59e0b" name="Processing" radius={[4, 4, 0, 0]} />
                <Bar dataKey="failed" fill="#ef4444" name="Failed" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchAnalytics;

